import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import React from 'react';
import { MAX_OPTION_LENGTH } from '../utils/validation';

interface OptionsManagerProps {
  options: string[];
  currentOption: string;
  optionsError: string;
  onOptionsChange: (options: string[]) => void;
  onCurrentOptionChange: (value: string) => void;
  onOptionsErrorChange: (error: string) => void;
}

export const OptionsManager: React.FC<OptionsManagerProps> = ({
  options,
  currentOption,
  optionsError,
  onOptionsChange,
  onCurrentOptionChange,
  onOptionsErrorChange,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const handleAddOption = () => {
    if (currentOption.trim()) {
      onOptionsChange([...options, currentOption.trim()]);
      onCurrentOptionChange('');
    }
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    onOptionsChange(newOptions);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${color.ui.divider.default.value}`,
        borderRadius: tokens.semantic.radius.lg.value,
        padding: '16px',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: tokens.semantic.fontWeight.emphasis.value,
          color: color.type.default.value,
          mb: '12px',
        }}
      >
        Options
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: '4px',
        }}
      >
        <Typography
          component="label"
          htmlFor="custom-field-option-input"
          sx={{
            fontSize: '14px',
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
            color: color.type.default.value,
          }}
        >
          Add new option
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: '12px', color: color.type.muted.value }}
        >
          {currentOption.length}/{MAX_OPTION_LENGTH}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          mb: '8px',
          alignItems: 'stretch',
        }}
      >
        <TextField
          id="custom-field-option-input"
          fullWidth
          value={currentOption}
          onChange={(e) => {
            if (e.target.value.length <= MAX_OPTION_LENGTH) {
              onCurrentOptionChange(e.target.value);
              if (optionsError) {
                onOptionsErrorChange('');
              }
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && currentOption.trim()) {
              e.preventDefault();
              handleAddOption();
            }
          }}
          placeholder="Add an option and press enter"
          data-testid="create-custom-field-option-input"
          error={!!optionsError}
        />
        <Button
          variant="contained"
          onClick={handleAddOption}
          disabled={!currentOption.trim()}
          data-testid="create-custom-field-add-option-button"
          sx={{
            textTransform: 'none',
            minWidth: '80px',
            backgroundColor: color.action.primary.default.value,
            color: color.surface.default.value,
            '&:hover': {
              backgroundColor: color.action.primary.default.value,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: color.ui.divider.default.value,
              color: color.type.muted.value,
            },
          }}
        >
          Add
        </Button>
      </Box>
      {!optionsError && (
        <Typography
          sx={{
            fontSize: '12px',
            color: color.type.muted.value,
            mb: '12px',
          }}
        >
          Retired options stay on cases that already use them and remain in
          exports but they cannot be selected on new entries.
        </Typography>
      )}
      {optionsError && (
        <Typography
          sx={{
            fontSize: '12px',
            color: color.status.error.default.value,
            mb: '12px',
          }}
          data-testid="create-custom-field-options-error"
        >
          {optionsError}
        </Typography>
      )}
      {options.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {options.map((option, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                backgroundColor: color.surface.subtle.value,
                borderRadius: tokens.semantic.radius.md.value,
              }}
              data-testid={`create-custom-field-option-${index}`}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: color.type.default.value,
                }}
              >
                {option}
              </Typography>
              <Typography
                component="button"
                type="button"
                onClick={() => handleRemoveOption(index)}
                sx={{
                  fontSize: '14px',
                  color: color.status.error.default.value,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                data-testid={`create-custom-field-remove-option-${index}`}
              >
                Remove
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
